import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Nav } from '../components/Nav';
import { UnifiedFooter, SectionHeading } from '../components';
import '../styles/global.css';
import { Link } from 'react-router-dom';
import { posts, BlogPost } from '../posts';

const categories = [
  'All Posts',
  'Engineering',
  'Security',
  'Company News',
  'Product',
  'AI',
];

const BlogPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('All Posts');
  const filteredPosts: BlogPost[] = selectedCategory === 'All Posts'
    ? posts
    : posts.filter((post: BlogPost) => post.category === selectedCategory);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { when: 'beforeChildren', staggerChildren: 0.1 },
    },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <motion.div
      className="min-h-screen bg-white text-dark-900 font-sans overflow-x-clip"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      variants={containerVariants}
    >
      {/* Navigation */}
      <Nav activeLink="blog" />

      {/* Blog Header */}
      <motion.section className="pt-40 pb-12 bg-white" variants={itemVariants}>
        <div className="page-padding">
          <div className="container-medium">
            <div className="max-w-3xl">
              <SectionHeading
                caption="BLOG"
                title={<span className="leading-tight">Insights on social engineering and identity verification for modern IT and security teams.</span>}
                description=""
                className="text-left"
              />
            </div>
            <div className="flex flex-wrap gap-3 mt-8 mb-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-5 py-2 rounded-full border text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    selectedCategory === cat
                      ? 'bg-blue-700 text-white border-blue-700 shadow'
                      : 'bg-white text-blue-700 border-blue-100 hover:bg-blue-50'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      {/* Blog Posts Grid */}
      <motion.section className="py-16 bg-white" variants={itemVariants}>
        <div className="page-padding">
          <div className="container-medium">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {filteredPosts.map((post: BlogPost, idx: number) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="block group"
                >
                  <motion.div
                    className="rounded-2xl border border-blue-100 bg-white shadow-md p-8 flex flex-col justify-between min-h-[320px] transition-transform group-hover:-translate-y-1 group-hover:shadow-lg"
                    variants={itemVariants}
                    whileHover={{ y: -4, boxShadow: '0 8px 32px rgba(0,0,0,0.10)' }}
                  >
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-mono text-blue-700 uppercase tracking-wider">{post.category}</span>
                        <span className="text-xs text-gray-400">{post.date}</span>
                      </div>
                      <h2 className="text-2xl font-semibold text-dark-900 mb-3 leading-tight group-hover:underline">{post.title}</h2>
                      <p className="text-base text-dark-600 mb-6">{post.description}</p>
                    </div>
                    <div className="flex items-center mt-auto pt-4 border-t border-blue-50">
                      <img src={post.avatar} alt={post.author} className="w-8 h-8 rounded-full mr-3" />
                      <span className="text-sm text-dark-700 font-medium">{post.author}</span>
                    </div>
                  </motion.div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      {/* Footer */}
      <UnifiedFooter itemVariants={itemVariants} />
    </motion.div>
  );
};

export default BlogPage; 